import { MATTERPORT_SID_REGEX } from "./matterport";

export interface MatterportPoint3 {
  x: number;
  y: number;
  z: number;
}

export interface MatterportLookAtOptions {
  moveToClosestSweep?: boolean;
  transitionTime?: number;
  mode?: string;
  transition?: string;
  offset?: MatterportPoint3;
}

/** The hosted viewer can expose newer APIs independently of our SDK package. */
export interface MatterportCapabilitiesSdk {
  App?: {
    Feature?: { SplatPresent?: string; MeshPresent?: string };
    features?: {
      subscribe(
        callback: (features: Record<string, boolean | undefined>) => void,
      ): { cancel(): void };
    };
  };
  __metadata__?: unknown;
  Camera?: {
    lookAt?(
      point: MatterportPoint3,
      options?: MatterportLookAtOptions,
    ): Promise<void>;
    TransitionType?: { FLY?: string; INSTANT?: string };
  };
  Link?: { createDeepLink?: () => Promise<string> };
}

export interface MatterportFeatures {
  readonly splatPresent: boolean | undefined;
  readonly meshPresent: boolean | undefined;
}

export interface MatterportRuntimeMetadata {
  readonly applicationVersion?: string;
  readonly bootstrapVersion?: string;
  readonly builderVersion?: string;
  readonly availableInterfaces?: Readonly<Record<string, string>>;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Unknown asset presence is different from a model confirmed to have no asset. */
export function subscribeMatterportFeatures(
  sdk: MatterportCapabilitiesSdk,
  callback: (features: MatterportFeatures) => void,
): () => void {
  let active = true;
  let subscription: { cancel(): void } | undefined;
  callback(Object.freeze({ splatPresent: undefined, meshPresent: undefined }));
  const features = sdk.App?.features;
  if (typeof features?.subscribe === "function") {
    const splatKey = sdk.App?.Feature?.SplatPresent ?? "feature.splatpresent";
    const meshKey = sdk.App?.Feature?.MeshPresent ?? "feature.meshpresent";
    try {
      subscription = features.subscribe((value) => {
        if (!active) return;
        const raw = record(value);
        const splat = raw?.[splatKey];
        const mesh = raw?.[meshKey];
        callback(Object.freeze({
          splatPresent: typeof splat === "boolean" ? splat : undefined,
          meshPresent: typeof mesh === "boolean" ? mesh : undefined,
        }));
      });
    } catch {
      // An older or disconnected runtime still supports the existing viewer.
    }
  }
  return () => {
    if (!active) return;
    active = false;
    try {
      subscription?.cancel();
    } catch {
      // Disconnect may already have disposed the observable.
    }
  };
}

function version(value: unknown): string | undefined {
  return typeof value === "string" && /^[\w][\w.+-]{0,127}$/.test(value)
    ? value
    : undefined;
}

/** Snapshot only documented version fields; never pass the raw SDK to logging. */
export function readMatterportRuntimeMetadata(
  sdk: MatterportCapabilitiesSdk,
): MatterportRuntimeMetadata | null {
  const raw = record(sdk.__metadata__);
  if (!raw) return null;
  const metadata: {
    applicationVersion?: string;
    bootstrapVersion?: string;
    builderVersion?: string;
    availableInterfaces?: Readonly<Record<string, string>>;
  } = {};
  for (const key of ["applicationVersion", "bootstrapVersion", "builderVersion"] as const) {
    const value = version(raw[key]);
    if (value !== undefined) metadata[key] = value;
  }
  const interfaces = record(raw.availableInterfaces);
  if (interfaces) {
    const entries = Object.entries(interfaces).slice(0, 32).flatMap(([key, value]) => {
      const validVersion = version(value);
      return /^[a-zA-Z][\w.-]{0,63}$/.test(key) &&
        !["constructor", "prototype"].includes(key) && validVersion !== undefined
        ? [[key, validVersion] as const]
        : [];
    });
    if (entries.length) metadata.availableInterfaces = Object.freeze(Object.fromEntries(entries));
  }
  return Object.keys(metadata).length ? Object.freeze(metadata) : null;
}

function validatePoint(point: MatterportPoint3, label: string): void {
  if (!point || ![point.x, point.y, point.z].every(Number.isFinite)) {
    throw new Error(`${label} must contain finite x, y, and z coordinates.`);
  }
}

/** False means unsupported; runtime failures reject so callers can show or fall back. */
export async function lookAtMatterportPoint(
  sdk: MatterportCapabilitiesSdk,
  point: MatterportPoint3,
  options?: MatterportLookAtOptions,
): Promise<boolean> {
  validatePoint(point, "Camera target");
  if (options?.offset !== undefined) validatePoint(options.offset, "Camera offset");
  if (options?.transitionTime !== undefined &&
      (!Number.isFinite(options.transitionTime) || options.transitionTime <= 0)) {
    throw new Error("Camera transition time must be a positive finite number.");
  }
  if (options?.moveToClosestSweep !== undefined && typeof options.moveToClosestSweep !== "boolean") {
    throw new Error("Camera sweep navigation must be a boolean.");
  }
  for (const key of ["mode", "transition"] as const) {
    const value = options?.[key];
    if (value !== undefined && (typeof value !== "string" || !value.trim())) {
      throw new Error(`Camera ${key} must be a nonempty string.`);
    }
  }
  if (typeof sdk.Camera?.lookAt !== "function") return false;
  await sdk.Camera.lookAt(point, options);
  return true;
}

const PRIVATE_LINK_PARAMETERS = new Set([
  "applicationkey", "apikey", "key", "token", "accesstoken", "refreshtoken",
  "authorization", "auth", "password", "secret", "clientsecret",
]);

function stripPrivateLinkParameters(params: URLSearchParams): void {
  for (const key of [...params.keys()]) {
    if (PRIVATE_LINK_PARAMETERS.has(key.toLowerCase().replace(/[-_]/g, ""))) {
      params.delete(key);
    }
  }
}

/** Use only for tours the caller already permits sharing outside the application. */
export async function createMatterportCurrentViewLink(
  sdk: MatterportCapabilitiesSdk,
): Promise<string | null> {
  if (typeof sdk.Link?.createDeepLink !== "function") return null;
  const raw = await sdk.Link.createDeepLink();
  if (typeof raw !== "string" || raw.length > 8192) {
    throw new Error("The viewer returned an invalid sharing link.");
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    throw new Error("The viewer returned an invalid sharing link.");
  }
  if (url.protocol !== "https:" || url.username || url.password || url.port ||
      !["my.matterport.com", "matterport.com"].includes(url.hostname) ||
      !/^\/show\/?$/.test(url.pathname) || !MATTERPORT_SID_REGEX.test(url.searchParams.get("m") ?? "")) {
    throw new Error("The viewer returned an invalid Matterport sharing link.");
  }
  stripPrivateLinkParameters(url.searchParams);
  // Current SDK links use query parameters; also sanitize legacy query-style fragments.
  if (url.hash) {
    const fragment = new URLSearchParams(url.hash.slice(1).replace(/^\?/, ""));
    stripPrivateLinkParameters(fragment);
    url.hash = fragment.toString();
  }
  return url.toString();
}
